/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  OcppLogsProperties,
  OcppLogsInitialProperties,
  OcppLogsResponse,
  OcppLogsRequest,
} from '../../interface/device-ocpp-logs.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import device from '../../utils/api/device';
import ocppLogs from '../../utils/api/ocpp-logs';

/**
 * Function to split the message string as object
 * @param messageArray
 * @returns
 */
function parseOcppMessage(messageString: string) {
  try {
    const parsed = JSON.parse(messageString) as [number, string, string | object, object?];
    const [messageTypeId, uniqueId, third, fourth] = parsed;

    // If `third` is an object, it's the payload and there's no action
    if (typeof third === 'object') {
      return {
        messageTypeId,
        uniqueId,
        payload: third,
      };
    }
    // `third` is the action, and `fourth` is the payload
    return {
      messageTypeId,
      uniqueId,
      // action: third,
      payload: fourth,
    };
  } catch (error) {
    console.error('Invalid OCPP message string:', error);
    return {};
  }
}

// a thunk to fetch users
export const fetchOcppLogs = createAsyncThunk<OcppLogsResponse, OcppLogsRequest>(
  'ocpp-logs/fetchOcppLogs',
  async (data: OcppLogsRequest, { dispatch }) => {
    try {
      const response = await device.fetchOcppLogs(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchOcppLogsFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('ocpp-logs/fetchOcppLogsFilters', async (_, { dispatch }) => {
  try {
    const response = await ocppLogs.listFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
      response?.data?.filter((eachData: any) => eachData.key !== filterType.DATE_RANGE),
    );
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param date
 * @returns
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const ocppFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: OcppLogsInitialProperties = {
  ocppLogs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: ocppFilters,
  filterListLoading: false,
};

// Create the slice
export const OcppLogsSlice = createSlice({
  name: 'ocpp-logs',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateOcppLogsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchOcppLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOcppLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.ocppLogs = action.payload?.results?.length
          ? action.payload.results?.map((eachLog: OcppLogsProperties) => ({
              ...eachLog,
              date: ConvertUTCtoDate(eachLog.created_at || ''),
              time: FormatUtcString(ConvertUTCtoHHMM(eachLog.created_at || '')),
              ...parseOcppMessage(eachLog.message),
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchOcppLogs.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchOcppLogsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchOcppLogsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchOcppLogsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateOcppLogsFilters } = OcppLogsSlice.actions;

// Export the reducer
export default OcppLogsSlice.reducer;
